import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { TileDeFato } from "../componentes/CalendarioUi";
import Carregando from "../componentes/Carregando";
import { TextoDaIA } from "../componentes/TextoDaIA";
import {
  alertaCasa,
  Destaque,
  eventoCasa,
  Fonte,
  LinhaDoTempo,
  ListaDeAlertas,
  Marca,
  type Alerta,
  type Evento,
  Procura,
  Secao,
  BalaoDoAssistente,
} from "../componentes/LeitorUi";
import {
  abrirAnalise,
  analisar,
  marcarItem,
  perguntar,
  type AnaliseAberta,
  type Fala,
  type ItemDoChecklist,
} from "../lib/leitorDeProcessos";
import { acha, comMilhar, NOME_DO_TIPO, paginasEmTexto } from "../lib/processos";

// O resultado de uma análise: quatro abas e a conversa.
//
// ---------------------------------------------------------------------------
// As abas são SUB-ROTA, não `useState`
// ---------------------------------------------------------------------------
// `/ferramenta/leitor-de-processos/<id>/checklist`. A doutrina da casa
// (`HubDpUi.tsx`): o perito volta pelo botão do navegador, manda o link de uma
// aba a um colega e recarrega sem cair na primeira. A origem guarda a aba em
// `useState`, e recarregar volta ao relatório.
//
// ---------------------------------------------------------------------------
// A busca procura NO RELATÓRIO, e diz isso
// ---------------------------------------------------------------------------
// Victor pediu a busca global em 02/09/2026, e ela entrou com uma diferença que
// não é de estilo.
//
// A da origem filtra as quatro abas e pinta o termo — só que olhando apenas o
// RESULTADO da IA. Um perito que procura "FGTS", recebe "nenhum resultado" e
// conclui que a palavra não está no processo estará errado nos dois sentidos
// possíveis: o termo pode estar no processo e não no que a IA extraiu, e pode
// estar numa página que a IA nem leu (31,4% do processo real de Victor ficou
// fora do teto).
//
// Aqui o campo se apresenta como "procurar no relatório", e o vazio não é um
// beco: ele oferece a MESMA pergunta ao assistente, que responde a partir do
// texto integral e cita a página. Prometer busca é prometer buscar no processo —
// então quem promete é o assistente, e a busca diz onde ela olha.
const ABAS = [
  { chave: "relatorio", rotulo: "Relatório" },
  { chave: "linha-do-tempo", rotulo: "Linha do tempo" },
  { chave: "checklist", rotulo: "Checklist" },
  { chave: "alertas", rotulo: "Alertas" },
] as const;

type Aba = (typeof ABAS)[number]["chave"];

export default function LeitorAnalise({ id }: { id: string }) {
  const { "*": resto = "" } = useParams();
  const [busca] = useSearchParams();
  const curso = busca.get("curso");
  const q = curso ? `?curso=${encodeURIComponent(curso)}` : "";

  const segmentos = resto.split("/").filter(Boolean);
  const pedida = segmentos[1] as Aba | undefined;
  const aba: Aba = ABAS.some((a) => a.chave === pedida) ? pedida! : "relatorio";

  const [dados, setDados] = useState<AnaliseAberta | null>(null);
  const [naoAchou, setNaoAchou] = useState(false);
  const [erroDeLeitura, setErroDeLeitura] = useState(false);

  // A busca é `useState`, e não sub-rota como as abas: ela não muda o CONTEÚDO
  // da tela, esconde parte dele, e ninguém manda a um colega o link do
  // "relatório filtrado por FGTS". A aba é endereço; o filtro é gesto.
  const [termo, setTermo] = useState("");
  // A pergunta que a busca vazia oferece ao assistente, esperando ser consumida
  // pela conversa. Estado do PAI porque quem dispara está acima do chat.
  const [perguntaPronta, setPerguntaPronta] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    void abrirAnalise(id)
      .then((d) => (d ? setDados(d) : setNaoAchou(true)))
      .catch(() => setErroDeLeitura(true));
  }, [id]);

  useEffect(recarregar, [recarregar]);

  // ── A ESPERA, enquanto a IA lê ────────────────────────────────────────────
  // A análise leva de 60 a 85 segundos MEDIDOS (03/09, com o processo real de
  // Victor e com textos de até 1 milhão de caracteres). Antes desta data a tela
  // de envio ficava parada esperando a Edge Function responder e só então
  // navegava; agora ela navega na hora, e é AQUI que a espera acontece.
  //
  // O estado `em_analise` tem DOIS significados, e confundi-los mostra a frase
  // errada: "está lendo agora" e "ficou pela metade" (a aba fechada, a conexão
  // caída). Quem separa é a IDADE: dentro da janela em que a function ainda
  // pode estar viva — os 150 s de wall clock dela, mais folga —, está lendo;
  // depois disso, ficou pela metade.
  //
  // Sem essa distinção, quem acabou de enviar um processo leria "A análise
  // ficou pela metade" enquanto ela está sendo feita, com um botão convidando a
  // gastar uma segunda chamada ao modelo por cima da primeira.
  const JANELA_DA_ANALISE_MS = 195000;
  const idadeMs = dados ? Date.now() - new Date(dados.analise.criado_em).getTime() : 0;
  const esperando =
    dados?.analise.situacao === "em_analise" && idadeMs < JANELA_DA_ANALISE_MS;

  useEffect(() => {
    if (!esperando) return;
    // 4 s: a análise não termina antes de 60, então perguntar mais rápido só
    // gasta ida de rede. E o `id` no fecho garante que o intervalo morre ao
    // trocar de análise.
    const t = setInterval(recarregar, 4000);
    return () => clearInterval(t);
  }, [esperando, recarregar]);

  // Quantos achados por aba, para o chip de cada uma. Sem este número o perito
  // procura um termo, vê a aba atual vazia e conclui que ele não está em lugar
  // nenhum — quando pode estar na aba ao lado.
  const achados = useMemo(() => {
    const vazio = { relatorio: 0, "linha-do-tempo": 0, checklist: 0, alertas: 0 };
    if (!dados || !termo.trim()) return { porAba: vazio, total: 0 };

    const r = dados.analise.resultado?.relatorio;
    const noRelatorio =
      [r?.identificacao, r?.objetoAcao, r?.objetoPericia].filter((t) => acha(t, termo)).length +
      (r?.pontosControvertidos ?? []).filter((p) =>
        acha(typeof p === "string" ? p : p.texto, termo),
      ).length +
      (r?.quesitos ?? []).filter((qq) => acha(qq.texto, termo) || acha(qq.pagina, termo)).length +
      (r?.documentosRelevantes ?? []).filter((d) => acha(d.nome, termo) || acha(d.pagina, termo))
        .length +
      (r?.documentosAnalisados ?? []).filter(
        (d) => acha(d.nome, termo) || acha(d.fase, termo) || acha(d.classe, termo),
      ).length;

    const porAba = {
      relatorio: noRelatorio,
      "linha-do-tempo": (dados.analise.resultado?.linhaDoTempo ?? []).filter((e) =>
        eventoCasa(e, termo),
      ).length,
      checklist: dados.itens.filter(
        (i) => acha(i.texto, termo) || acha(i.pagina, termo) || acha(i.fonte, termo),
      ).length,
      alertas: (dados.analise.resultado?.alertas ?? []).filter((a) => alertaCasa(a, termo)).length,
    };
    return { porAba, total: Object.values(porAba).reduce((s, n) => s + n, 0) };
  }, [dados, termo]);

  if (naoAchou) {
    return (
      <section className="surface rounded-cf p-6">
        <h1 className="font-display text-xl font-bold text-titulo">Análise não encontrada</h1>
        <p className="mt-2 text-sm text-tinta">
          Esta análise não existe ou não é sua. Talvez ela tenha sido apagada.
        </p>
        <Link to={`/ferramenta/leitor-de-processos${q}`} className="pill-cta mt-4 inline-flex">
          Ver os meus processos
        </Link>
      </section>
    );
  }

  if (erroDeLeitura) {
    return (
      <p className="rounded-cf border border-erro-borda bg-erro-fundo p-4 text-sm text-erro-tinta">
        Não conseguimos carregar esta análise agora. Recarregue a página.
      </p>
    );
  }

  if (!dados) return <Carregando />;

  const a = dados.analise;
  const truncou =
    a.caracteres_enviados !== null && a.caracteres_enviados < a.caracteres;

  return (
    <>
      <Cabine
        trilha={{ para: `/ferramenta/leitor-de-processos${q}`, rotulo: "Leitor de Processos" }}
        rotulo={NOME_DO_TIPO[a.tipo] ?? a.tipo}
        titulo={a.titulo}
        descricao={
          a.situacao === "ok"
            ? dados.analise.resultado?.relatorio?.identificacao
            : undefined
        }
        fatos={
          a.situacao === "ok" ? (
            <>
              {/* O rótulo "Páginas lidas" com o TOTAL do processo é falso
                  quando houve corte, e a captura de 03/09 pegou os dois números
                  se contradizendo na mesma tela: o tile dizia "Páginas lidas
                  98" e o aviso três blocos abaixo dizia "leu até a página 66 de
                  98". É a família do contador do checklist em três lugares —
                  aqui o mesmo fato aparecia duas vezes com valores diferentes, e
                  o mais visível era o errado. */}
              <TileDeFato
                rotulo={truncou && a.paginas_lidas ? "Páginas lidas" : "Páginas"}
                valor={comMilhar(truncou && a.paginas_lidas ? a.paginas_lidas : a.paginas)}
                apoio={
                  truncou && a.paginas_lidas
                    ? `de ${comMilhar(a.paginas)} do processo`
                    : `em ${dados.arquivos.length} arquivo${dados.arquivos.length === 1 ? "" : "s"}`
                }
              />
              <TileDeFato
                rotulo="Checklist"
                valor={`${dados.itens.filter((i) => i.feito).length} de ${dados.itens.length}`}
                apoio="o que você já marcou"
              />
              <TileDeFato
                rotulo="Alertas"
                valor={comMilhar(dados.analise.resultado?.alertas?.length ?? 0)}
                apoio="pontos de atenção"
              />
            </>
          ) : undefined
        }
      />

      {/* O corte é DITO. A origem trunca em 120.000 caracteres e não conta a
          ninguém, então o perito lê um relatório com aparência de completo sobre
          o primeiro terço do processo. */}
      {truncou && (
        <p className="mt-4 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo p-4 text-sm text-alerta-tinta">
          <strong className="font-semibold">Este processo não foi lido por inteiro.</strong>{" "}
          {/* O número de páginas vem CONTADO do servidor (`paginas_lidas`,
              000173), não estimado aqui. Dizer "até a página N" é o que o perito
              usa: ele sabe exatamente o que reenviar numa segunda análise. */}
          {a.paginas_lidas ? (
            <>
              A IA leu até a <strong className="font-semibold">página {a.paginas_lidas}</strong> de{" "}
              {a.paginas} — as {paginasEmTexto(a.paginas - a.paginas_lidas)} seguintes ficaram
              de fora ({comMilhar(a.caracteres_enviados!)} dos {comMilhar(a.caracteres)}{" "}
              caracteres). Para analisar o resto, envie essas páginas numa análise nova.
            </>
          ) : (
            <>
              A IA leu {comMilhar(a.caracteres_enviados!)} dos {comMilhar(a.caracteres)}{" "}
              caracteres do texto. O que está abaixo se refere só à parte lida.
            </>
          )}
        </p>
      )}

      {esperando && <LendoAgora dados={dados} />}

      {a.situacao !== "ok" && !esperando && (
        <NaoDeuCerto dados={dados} aoTerminar={recarregar} />
      )}

      {a.situacao === "ok" && (
        <>
          <Procura
            termo={termo}
            aoTrocar={setTermo}
            achados={achados}
            // Só a pergunta. Rolar até o campo era necessário enquanto a
            // conversa era a última seção da página; agora o balão ABRE, e o
            // `scrollIntoView` procuraria um campo que ainda não está no DOM.
            aoPerguntar={(t) => setPerguntaPronta(`Onde o processo fala de "${t}"?`)}
          />

          <nav aria-label="Seções da análise" className="mt-6 flex flex-wrap gap-2">
            {ABAS.map((x) => (
              <Link
                key={x.chave}
                to={`/ferramenta/leitor-de-processos/${id}/${x.chave}${q}`}
                aria-current={aba === x.chave ? "page" : undefined}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                  aba === x.chave
                    ? "bg-azul text-white"
                    : "border-[1.5px] border-borda text-tinta hover:border-laranja"
                }`}
              >
                {x.rotulo}
                {x.chave === "checklist" && dados.itens.length > 0 && (
                  <span className="ml-1.5 tabular-nums opacity-80">
                    {dados.itens.filter((i) => i.feito).length}/{dados.itens.length}
                  </span>
                )}
                {/* Quantos achados a busca tem NESTA aba. Sem isto, o perito
                    procura um termo, vê a aba atual vazia e conclui que ele não
                    está em lugar nenhum — quando pode estar na aba ao lado. */}
                {termo.trim() && achados.porAba[x.chave] > 0 && (
                  <span className="ml-1.5 rounded-full bg-laranja/20 px-1.5 text-[0.6875rem] font-semibold text-laranja-texto tabular-nums">
                    {achados.porAba[x.chave]}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="mt-4">
            {aba === "relatorio" && <AbaRelatorio dados={dados} termo={termo} />}
            {aba === "linha-do-tempo" && (
              <LinhaDoTempo
                eventos={filtrarEventos(dados.analise.resultado?.linhaDoTempo ?? [], termo)}
                termo={termo}
                escondidos={
                  (dados.analise.resultado?.linhaDoTempo ?? []).length -
                  filtrarEventos(dados.analise.resultado?.linhaDoTempo ?? [], termo).length
                }
              />
            )}
            {aba === "checklist" && (
              <AbaChecklist
                itens={dados.itens}
                termo={termo}
                aoMarcar={(id, feito) =>
                  // O estado do CHECKLIST é do pai, e não só da aba: os
                  // contadores da cabine e da aba leem daqui. Recarregar a
                  // análise inteira a cada tique custaria uma ida de rede para
                  // trocar um booleano que já sabemos.
                  setDados((d) =>
                    d
                      ? {
                          ...d,
                          itens: d.itens.map((i) => (i.id === id ? { ...i, feito } : i)),
                        }
                      : d,
                  )
                }
              />
            )}
            {aba === "alertas" && (
              <ListaDeAlertas
                alertas={filtrarAlertas(dados.analise.resultado?.alertas ?? [], termo)}
                termo={termo}
                escondidos={
                  (dados.analise.resultado?.alertas ?? []).length -
                  filtrarAlertas(dados.analise.resultado?.alertas ?? [], termo).length
                }
              />
            )}
          </div>

          <Conversa
            id={id}
            inicial={dados.conversa}
            perguntaPronta={perguntaPronta}
            aoConsumirPergunta={() => setPerguntaPronta(null)}
          />
        </>
      )}
    </>
  );
}

// ===========================================================================
// A análise que não terminou
// ===========================================================================
// Dois estados chegam aqui: `em_analise` (a chamada não voltou — rede, aba
// fechada) e `falha` (o modelo recusou ou não respondeu). Nos dois o texto do
// processo continua salvo, então o botão é "analisar de novo" e NÃO pede os PDFs
// outra vez. É a contrapartida de guardar o texto.
/**
 * A IA está lendo AGORA — a tela dizendo o que está acontecendo, em vez de
 * "ficou pela metade" (que é o que o mesmo estado significa depois da janela).
 *
 * O tempo MEDIDO entra no texto: dizer "de 1 a 2 minutos" é o que impede o
 * perito de concluir que a tela travou e recarregar por cima. Número medido, não
 * arredondado para soar rápido.
 */
function LendoAgora({ dados }: { dados: AnaliseAberta }) {
  const a = dados.analise;
  return (
    <section className="mt-4 rounded-cf border border-borda bg-chip/40 p-5" aria-live="polite">
      <div className="flex items-start gap-3">
        {/* `motion-safe`: quem pediu menos movimento no sistema não recebe o
            giro. A peça continua dizendo o mesmo sem ele. */}
        <span
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 rounded-full border-2 border-laranja border-t-transparent motion-safe:animate-spin"
        />
        <div className="min-w-0">
          <h2 className="font-display text-base font-bold text-titulo">
            A IA está lendo o processo
          </h2>
          <p className="mt-1 text-sm text-tinta">
            São {comMilhar(a.caracteres)} caracteres em {a.paginas} páginas, e isso leva de 1 a
            2 minutos. Esta tela se atualiza sozinha — não precisa recarregar.
          </p>
          {/* Sem promessa sobre fechar a aba: não foi medido se a Edge Function
              sobrevive ao cliente desconectar (o `req.signal` do Deno pode
              abortar), e afirmar isso ao perito seria vender o que não se sabe.
              O que É verdade e importa: o texto está salvo, então nenhuma
              interrupção custa o upload de novo. */}
          <p className="mt-2 text-xs text-cinza">
            O texto do processo já está salvo — se algo interromper a leitura, você não vai
            precisar enviar os PDFs outra vez.
          </p>
        </div>
      </div>
    </section>
  );
}

function NaoDeuCerto({ dados, aoTerminar }: { dados: AnaliseAberta; aoTerminar: () => void }) {
  const [tentando, setTentando] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const a = dados.analise;

  return (
    <section className="surface mt-6 rounded-cf p-5">
      <h2 className="font-display text-lg font-bold text-titulo">
        {a.situacao === "em_analise" ? "A análise ficou pela metade" : "A análise não foi concluída"}
      </h2>

      <p className="mt-2 text-sm text-tinta">
        {a.situacao === "em_analise"
          ? "A leitura não chegou ao fim — pode ter sido a conexão, ou a aba fechada antes de terminar."
          : "O modelo não conseguiu ler este processo desta vez."}
      </p>

      {/* O motivo, NOMEADO. Ele vem classificado do módulo (`limite`,
          `recusado`, `resposta_invalida`…) e não é uma frase genérica: as causas
          pedem ações diferentes do perito. */}
      {a.erro && (
        <p className="mt-2 rounded-cf border border-borda bg-chip/40 p-3 font-mono text-xs text-cinza">
          {a.erro}
        </p>
      )}

      <p className="mt-3 text-sm text-tinta">
        O texto do processo continua salvo aqui — {comMilhar(a.caracteres)} caracteres em{" "}
        {a.paginas} páginas. Não é preciso enviar os PDFs de novo.
      </p>

      <button
        type="button"
        disabled={tentando}
        onClick={() => {
          setTentando(true);
          setMensagem(null);
          void analisar(a.id).then((r) => {
            setTentando(false);
            if (r.ok) aoTerminar();
            else setMensagem(r.mensagem ?? "Não deu certo desta vez.");
          });
        }}
        className={tentando ? "pill-desligada mt-4" : "pill-cta mt-4"}
      >
        {tentando ? "A IA está lendo o processo…" : "Analisar de novo"}
      </button>

      {mensagem && (
        <p className="mt-3 rounded-cf border border-erro-borda bg-erro-fundo p-3 text-sm text-erro-tinta">
          {mensagem}
        </p>
      )}
    </section>
  );
}

/** Os filtros da busca, num lugar só — a tela filtra e o chip da aba conta o
 *  MESMO conjunto. Duas cópias divergiriam, e a que divergisse em silêncio
 *  faria o chip prometer um achado que a aba não mostra. */
function filtrarEventos(eventos: Evento[], termo: string): Evento[] {
  return termo.trim() ? eventos.filter((e) => eventoCasa(e, termo)) : eventos;
}
function filtrarAlertas(alertas: Alerta[], termo: string): Alerta[] {
  return termo.trim() ? alertas.filter((a) => alertaCasa(a, termo)) : alertas;
}

// ===========================================================================
// Aba 1 — o relatório
// ===========================================================================
function AbaRelatorio({ dados, termo }: { dados: AnaliseAberta; termo: string }) {
  const r = dados.analise.resultado?.relatorio;
  const nomes = dados.arquivos.map((a) => a.nome);

  if (!r) {
    return (
      <p className="surface rounded-cf p-5 text-sm text-tinta">
        O modelo não devolveu o relatório desta vez. As outras abas podem ter conteúdo.
      </p>
    );
  }

  const docs = r.documentosAnalisados ?? [];

  return (
    <div className="grid gap-4">
      {docs.length > 1 && (
        <Secao titulo="Documentos analisados">
          <ul className="grid gap-2">
            {docs.map((d, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                <span className="font-medium text-tinta">
                  <Marca texto={d.nome ?? ""} termo={termo} />
                </span>
                {/* A fase e a classe também são PINTADAS: o chip da aba conta
                    os três campos (nome, fase, classe), e pintar só um deles
                    fazia o chip prometer um achado que a tela não mostrava.
                    Quem conta e quem destaca têm de olhar o mesmo conjunto. */}
                {d.fase && (
                  <span className="rounded-full bg-chip px-2 py-0.5 text-[0.6875rem] font-semibold text-chip-tinta">
                    <Marca texto={d.fase} termo={termo} />
                  </span>
                )}
                {d.classe && (
                  <span className="text-xs text-cinza">
                    <Marca texto={d.classe} termo={termo} />
                  </span>
                )}
                {d.paginas && <span className="text-xs text-cinza">págs. {d.paginas}</span>}
              </li>
            ))}
          </ul>
        </Secao>
      )}

      <Secao titulo="Identificação do processo" paraCopiar={r.identificacao}>
        <Destaque texto={r.identificacao} termo={termo} />
      </Secao>

      <Secao titulo="Objeto da ação" paraCopiar={r.objetoAcao}>
        <Destaque texto={r.objetoAcao} termo={termo} />
      </Secao>

      <Secao titulo="Objeto da perícia" paraCopiar={r.objetoPericia}>
        <Destaque texto={r.objetoPericia} termo={termo} />
      </Secao>

      <Secao
        titulo="Pontos controvertidos"
        paraCopiar={(r.pontosControvertidos ?? [])
          .map((p) => (typeof p === "string" ? p : p.texto ?? ""))
          .filter(Boolean)
          .map((t) => `• ${t}`)
          .join("\n")}
      >
        {(r.pontosControvertidos ?? []).length === 0 ? (
          <Vazio o="ponto controvertido" />
        ) : (
          <ul className="grid gap-2">
            {(r.pontosControvertidos ?? []).map((p, i) => {
              const texto = typeof p === "string" ? p : p.texto ?? "";
              const fonte = typeof p === "string" ? undefined : p.fonte;
              return (
                <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                  <span className="flex-1">
                    <Marca texto={texto} termo={termo} />
                  </span>
                  <Fonte nome={fonte} nomes={nomes} />
                </li>
              );
            })}
          </ul>
        )}
      </Secao>

      <Secao
        titulo="Quesitos"
        paraCopiar={(r.quesitos ?? [])
          .map((qq, i) => `${i + 1}. ${qq.texto ?? ""}${qq.pagina ? ` (${qq.pagina})` : ""}`)
          .join("\n")}
      >
        {(r.quesitos ?? []).length === 0 ? (
          <Vazio o="quesito" />
        ) : (
          <ol className="grid gap-2">
            {(r.quesitos ?? []).map((qq, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                <span className="font-mono text-xs text-cinza tabular-nums">{i + 1}.</span>
                <span className="flex-1">
                  <Marca texto={qq.texto ?? ""} termo={termo} />
                </span>
                <Fonte nome={qq.fonte} nomes={nomes} />
                {qq.pagina && <span className="text-xs whitespace-nowrap text-cinza">{qq.pagina}</span>}
              </li>
            ))}
          </ol>
        )}
      </Secao>

      <Secao
        titulo="Documentos relevantes"
        paraCopiar={(r.documentosRelevantes ?? [])
          .map((d) => `• ${d.nome ?? ""}${d.pagina ? ` (${d.pagina})` : ""}`)
          .join("\n")}
      >
        {(r.documentosRelevantes ?? []).length === 0 ? (
          <Vazio o="documento" />
        ) : (
          <ul className="grid gap-2">
            {(r.documentosRelevantes ?? []).map((d, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 text-sm text-tinta">
                <span className="flex-1">
                  <Marca texto={d.nome ?? ""} termo={termo} />
                </span>
                <Fonte nome={d.fonte} nomes={nomes} />
                {d.pagina && <span className="text-xs whitespace-nowrap text-cinza">{d.pagina}</span>}
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </div>
  );
}

function Vazio({ o }: { o: string }) {
  return (
    <p className="text-sm text-cinza">
      A IA não identificou nenhum {o} no trecho lido. Pergunte ao assistente abaixo — ele
      responde a partir do texto do processo.
    </p>
  );
}

// ===========================================================================
// Aba 3 — o checklist, que GRAVA
// ===========================================================================
// O conserto do defeito central da origem: lá a caixa é `defaultChecked` sem
// `onCheckedChange`, então marcar não gravava nem em memória — o contador ficava
// preso em 0/N e o filtro "Concluídos" nunca achava nada.
function AbaChecklist({
  itens,
  termo,
  aoMarcar,
}: {
  itens: ItemDoChecklist[];
  termo: string;
  aoMarcar: (id: string, feito: boolean) => void;
}) {
  const [estado, setEstado] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(itens.map((i) => [i.id, i.feito])),
  );
  const [filtro, setFiltro] = useState<"todos" | "pendentes" | "feitos">("todos");
  const [naoGravou, setNaoGravou] = useState<string | null>(null);

  // O filtro é `useState` e não sub-rota de propósito: ele não muda o CONTEÚDO
  // da tela, só esconde parte dela, e o perito não manda a ninguém o link do
  // "checklist filtrado por pendentes".
  const visiveis = useMemo(
    () =>
      itens.filter((i) => {
        const feito = estado[i.id] ?? i.feito;
        if (filtro === "pendentes") return !feito;
        if (filtro === "feitos") return feito;
        return true;
      }),
    [itens, estado, filtro],
  );

  if (itens.length === 0) {
    return (
      <p className="surface rounded-cf p-5 text-sm text-tinta">
        A IA não gerou itens de checklist para este processo.
      </p>
    );
  }

  const feitos = itens.filter((i) => estado[i.id] ?? i.feito).length;

  async function alternar(item: ItemDoChecklist) {
    const novo = !(estado[item.id] ?? item.feito);
    setEstado((e) => ({ ...e, [item.id]: novo }));
    // O pai também precisa saber: o TILE da cabine e o CHIP da aba contam os
    // itens feitos, e sem este aviso eles ficam presos no número do carregamento
    // — foi a captura de 02/09 que mostrou "0 de 3" no alto com "1 de 3
    // marcados" embaixo, na mesma tela.
    //
    // É o defeito da origem por outro caminho: lá o contador ficava em 0/N
    // porque nada gravava; aqui grava, e o contador de cima não ouvia.
    aoMarcar(item.id, novo);
    setNaoGravou(null);

    const ok = await marcarItem(item.id, novo);
    if (!ok) {
      // Erro de ESCRITA não derruba a tela. A cicatriz do calendário: um tique
      // que não gravou trocava o mês inteiro pela mensagem "não conseguimos
      // carregar", apagando o que o contador estava lendo. Aqui a peça que
      // falhou é que informa, e ela diz que o resto continua ali.
      setEstado((e) => ({ ...e, [item.id]: !novo }));
      aoMarcar(item.id, !novo);
      setNaoGravou(item.id);
    }
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {(["todos", "pendentes", "feitos"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFiltro(f)}
            aria-pressed={filtro === f}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              filtro === f
                ? "bg-azul text-white"
                : "border border-borda text-cinza hover:border-laranja hover:text-tinta"
            }`}
          >
            {f === "todos" ? "Todos" : f === "pendentes" ? "Pendentes" : "Concluídos"}
          </button>
        ))}
        <span className="ml-auto text-xs text-cinza tabular-nums">
          {feitos} de {itens.length} marcados
        </span>
      </div>

      {visiveis.length === 0 ? (
        <p className="surface rounded-cf p-5 text-sm text-tinta">
          {filtro === "feitos"
            ? "Você ainda não marcou nenhum item."
            : "Todos os itens estão marcados."}
        </p>
      ) : (
        <ul className="grid gap-2">
          {visiveis.map((i) => {
            const feito = estado[i.id] ?? i.feito;
            return (
              <li key={i.id} className="surface rounded-cf p-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={feito}
                    onChange={() => void alternar(i)}
                    className="mt-0.5 size-4 accent-azul"
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm ${feito ? "text-cinza line-through" : "text-tinta"}`}
                    >
                      <Marca texto={i.texto} termo={termo} />
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-2">
                      {i.pagina && <span className="text-xs text-cinza">{i.pagina}</span>}
                      {i.fonte && <span className="text-xs text-cinza">{i.fonte}</span>}
                    </span>
                  </span>
                </label>
                {naoGravou === i.id && (
                  <p className="mt-2 text-xs text-erro-tinta">
                    Não deu para gravar esta marcação. O resto da análise continua aqui —
                    tente marcar de novo.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ===========================================================================
// A conversa
// ===========================================================================
const SUGESTOES = [
  "Resuma o processo",
  "Quais os valores envolvidos?",
  "Quais os prazos importantes?",
  "Quais os riscos para o perito?",
];

function Conversa({
  id,
  inicial,
  perguntaPronta,
  aoConsumirPergunta,
}: {
  id: string;
  inicial: Fala[];
  /** A pergunta que a busca vazia ofereceu — chega pronta para o campo. */
  perguntaPronta: string | null;
  aoConsumirPergunta: () => void;
}) {
  const [falas, setFalas] = useState<Fala[]>(inicial);
  const [pergunta, setPergunta] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // O balão nasce FECHADO, e a conversa gravada aparece no selo do botão. Abrir
  // sozinho cobriria o relatório que o perito veio ler — e quem tem conversa
  // salva sabe que ela está lá porque o número está no botão.
  const [aberto, setAberto] = useState(false);
  const corpo = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const navegar = useNavigate();

  // A rolagem é do CORPO DO BALÃO, por `scrollTop` — nunca `scrollIntoView`.
  // O segundo rola o ancestral rolável mais próximo e, quando o balão está no
  // fim de uma página longa, leva a PÁGINA junto: a resposta da IA arrastaria a
  // vista do perito para fora do trecho que ele está lendo. Com o chat no fim
  // da página isso era inofensivo; num painel flutuante seria um defeito.
  useEffect(() => {
    const c = corpo.current;
    if (!c) return;
    c.scrollTo({
      top: c.scrollHeight,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [falas, aberto]);

  // A pergunta que a busca vazia ofereceu chega PRONTA no campo, e não enviada:
  // o perito lê, ajusta se quiser e manda. Disparar sozinho gastaria uma chamada
  // ao modelo por um clique que era só um atalho de digitação.
  //
  // Ela ABRE o balão, que é o que a busca precisava e antes custava um
  // `scrollIntoView` até o fim da página.
  useEffect(() => {
    if (!perguntaPronta) return;
    setPergunta(perguntaPronta);
    setAberto(true);
    aoConsumirPergunta();
    // O foco espera o painel existir no DOM — antes disso o `campo` é nulo.
    setTimeout(() => campo.current?.focus(), 0);
  }, [perguntaPronta, aoConsumirPergunta]);

  async function enviar(texto: string) {
    const limpo = texto.trim();
    if (!limpo || ocupado) return;

    setOcupado(true);
    setErro(null);
    setPergunta("");
    setFalas((f) => [...f, { papel: "user", conteudo: limpo }, { papel: "assistant", conteudo: "" }]);

    let resposta = "";
    const r = await perguntar(id, limpo, (pedaco) => {
      resposta += pedaco;
      setFalas((f) => {
        const copia = f.slice();
        copia[copia.length - 1] = { papel: "assistant", conteudo: resposta };
        return copia;
      });
    });

    setOcupado(false);
    if (!r.ok) {
      // A fala vazia sai da lista: deixar uma bolha em branco na tela parece
      // resposta perdida, e o erro fica dito uma vez, no lugar certo.
      setFalas((f) => f.slice(0, -1));
      setErro(r.mensagem);
    }
    setTimeout(() => campo.current?.focus(), 50);
  }

  return (
    <BalaoDoAssistente
      aberto={aberto}
      aoTrocar={(v) => {
        setAberto(v);
        // Abrir um chat é o gesto de querer digitar, então o foco vai ao campo.
        if (v) setTimeout(() => campo.current?.focus(), 0);
      }}
      titulo="Assistente do processo"
      apoio="Responde só do texto que você enviou, com a página de cada trecho."
      selo={falas.filter((f) => f.papel === "user").length}
      rotuloFechado="Perguntar sobre o processo"
      corpoRef={corpo}
      rodape={
        <>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void enviar(pergunta);
            }}
          >
            <label htmlFor="pergunta-do-processo" className="sr-only">
              A sua pergunta sobre o processo
            </label>
            <input
              ref={campo}
              id="pergunta-do-processo"
              value={pergunta}
              onChange={(e) => setPergunta(e.target.value)}
              disabled={ocupado}
              maxLength={2000}
              placeholder="Pergunte sobre este processo…"
              className="min-w-0 flex-1 rounded-full border border-borda bg-superficie px-4 py-2.5 text-sm text-tinta placeholder:text-cinza focus:border-azul focus:outline-none"
            />
            <button
              type="submit"
              disabled={ocupado || !pergunta.trim()}
              aria-label={ocupado ? "Respondendo" : "Enviar a pergunta"}
              className={`shrink-0 ${ocupado || !pergunta.trim() ? "pill-desligada" : "pill-cta"}`}
            >
              {ocupado ? "…" : "Enviar"}
            </button>
          </form>

          {erro && (
            <p className="mt-2 rounded-cf border border-erro-borda bg-erro-fundo p-2.5 text-xs text-erro-tinta">
              {erro}
            </p>
          )}
        </>
      }
    >
      {falas.length === 0 && (
        <>
          <p className="text-sm text-tinta">
            Pergunte o que quiser sobre o processo. Se a informação não estiver nos PDFs, o
            assistente diz que não encontrou — em vez de supor.
          </p>
          <div className="mt-4 grid gap-2">
            {SUGESTOES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void enviar(s)}
                disabled={ocupado}
                className="rounded-full border border-borda px-3 py-1.5 text-left text-xs text-tinta transition hover:border-laranja"
              >
                {s}
              </button>
            ))}
          </div>
        </>
      )}

      {falas.length > 0 && (
        <div className="grid gap-3">
          {falas.map((f, i) => (
            <div
              key={i}
              className={`rounded-cf px-3.5 py-2.5 text-sm ${
                f.papel === "user"
                  ? "ml-auto max-w-[88%] bg-chip text-chip-tinta"
                  : "border border-borda bg-superficie-2"
              }`}
            >
              {f.papel === "user" ? (
                <p className="whitespace-pre-wrap">{f.conteudo}</p>
              ) : f.conteudo ? (
                <TextoDaIA texto={f.conteudo} />
              ) : (
                <p className="text-cinza">Lendo o processo…</p>
              )}
            </div>
          ))}

          <p className="pt-1 text-xs text-cinza">
            Esta conversa fica salva com a análise.{" "}
            <button
              type="button"
              onClick={() => navegar(0)}
              className="underline hover:text-tinta"
            >
              Recarregar
            </button>{" "}
            para ver a versão gravada.
          </p>
        </div>
      )}
    </BalaoDoAssistente>
  );
}
